import React, { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, BarChart, Bar, ComposedChart, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Zap, Flame, Factory, IndianRupee, AlertTriangle, Settings } from "lucide-react";

function Tile({ icon: Icon, label, value, unit, tone = "sky" }) {
  const map = { sky: "text-sky-700 bg-sky-50 border-sky-100", amber: "text-amber-700 bg-amber-50 border-amber-100",
                green: "text-emerald-700 bg-emerald-50 border-emerald-100", slate: "text-slate-700 bg-slate-50 border-slate-100" };
  return (
    <Card className="p-5"><div className="flex items-start justify-between">
      <div><div className="label-cap text-slate-500">{label}</div>
        <div className="mt-2 text-2xl font-light" style={{ fontFamily: "Chivo" }}>{value} {unit && <span className="text-xs text-slate-500">{unit}</span>}</div></div>
      <div className={`h-9 w-9 rounded-md border flex items-center justify-center ${map[tone]}`}><Icon className="h-4 w-4" /></div>
    </div></Card>
  );
}

export default function Energy() {
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("all");
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [rates, setRates] = useState({ electricity_per_kwh: 8, diesel_per_litre: 95, dg_fuel_per_litre: 0, effective_from: new Date().toISOString().slice(0,10) });

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)); }, []);
  useEffect(() => {
    const params = { days }; if (pid !== "all") params.project_id = pid;
    api.get("/energy/performance", { params }).then((r) => setData(r.data));
  }, [pid, days]);

  const openSettings = async () => {
    if (pid === "all") return toast.error("Select a specific project first");
    const r = await api.get(`/projects/${pid}/energy-settings`);
    setRates({ ...r.data.current, effective_from: r.data.current.effective_from?.slice(0, 10) || new Date().toISOString().slice(0, 10) });
    setSettingsOpen(true);
  };
  const saveRates = async () => {
    try { await api.post(`/projects/${pid}/energy-settings`, rates); toast.success("Rates saved"); setSettingsOpen(false);
      const params = { days }; if (pid !== "all") params.project_id = pid;
      const r = await api.get("/energy/performance", { params }); setData(r.data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const s = data?.summary || {};
  const anomalies = (data?.series || []).filter((x) => x.anomaly).length;

  return (
    <AppLayout title="Energy Performance" subtitle="Specific energy consumption & cost per MT of AFR produced"
      actions={
        <div className="flex gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="energy-days-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{[7, 30, 90, 180, 365].map((d) => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}</SelectContent>
          </Select>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger className="w-48" data-testid="energy-project-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All projects</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={openSettings} data-testid="energy-settings-button"><Settings className="h-3.5 w-3.5 mr-1" /> Rates</Button>
        </div>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-3 mb-6">
        <Tile icon={Factory} label="Total AFR" value={s.total_afr ?? "—"} unit="MT" tone="sky" />
        <Tile icon={Zap} label="Total Electricity" value={s.total_kwh ?? "—"} unit="kWh" tone="sky" />
        <Tile icon={Flame} label="Total Diesel" value={s.total_diesel ?? "—"} unit="L" tone="amber" />
        <Tile icon={Zap} label="kWh / MT" value={s.kwh_per_mt ?? "—"} tone="green" />
        <Tile icon={Flame} label="Litre / MT" value={s.l_per_mt ?? "—"} tone="amber" />
        <Tile icon={IndianRupee} label="Electricity Cost" value={`₹${(s.electricity_cost || 0).toLocaleString("en-IN")}`} tone="slate" />
        <Tile icon={IndianRupee} label="Diesel Cost" value={`₹${(s.diesel_cost || 0).toLocaleString("en-IN")}`} tone="slate" />
        <Tile icon={IndianRupee} label="Total Energy Cost" value={`₹${(s.total_energy_cost || 0).toLocaleString("en-IN")}`} tone="slate" />
        <Tile icon={IndianRupee} label="Cost / MT" value={`₹${s.cost_per_mt ?? "—"}`} tone="green" />
        <Tile icon={AlertTriangle} label="Anomalies" value={anomalies} unit="days" tone={anomalies ? "amber" : "slate"} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="label-cap text-slate-500">Production vs Electricity</div>
          <div className="text-lg font-semibold mb-4" style={{ fontFamily: "Chivo" }}>AFR Output & Power Consumption</div>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={data?.series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis yAxisId="l" tick={{ fontSize: 11 }} /><YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="l" dataKey="kwh" fill="#0EA5E9" name="Electricity (kWh)" />
              <Line yAxisId="r" dataKey="afr" stroke="#0C4A6E" strokeWidth={2} dot={false} name="AFR (MT)" />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5">
          <div className="label-cap text-slate-500">Specific Consumption Trend</div>
          <div className="text-lg font-semibold mb-4" style={{ fontFamily: "Chivo" }}>kWh/MT & L/MT</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data?.series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis yAxisId="l" tick={{ fontSize: 11 }} /><YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="l" dataKey="kwh_per_mt" stroke="#0C4A6E" strokeWidth={2} dot={false} name="kWh / MT" />
              <Line yAxisId="r" dataKey="l_per_mt" stroke="#D97706" strokeWidth={2} dot={false} name="L / MT" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5 xl:col-span-2">
          <div className="label-cap text-slate-500">Energy Cost Trend</div>
          <div className="text-lg font-semibold mb-4" style={{ fontFamily: "Chivo" }}>Daily Electricity + Diesel Cost</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data?.series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="elec_cost" stackId="a" fill="#0EA5E9" name="Electricity ₹" />
              <Bar dataKey="diesel_cost" stackId="a" fill="#D97706" name="Diesel ₹" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="energy-rates-dialog">
          <DialogHeader><DialogTitle>Energy Rates Configuration</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-cap">Electricity (₹/kWh)</Label><Input type="number" step="0.01" data-testid="rate-electricity-input" value={rates.electricity_per_kwh} onChange={(e) => setRates({ ...rates, electricity_per_kwh: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label className="label-cap">Diesel (₹/L)</Label><Input type="number" step="0.01" data-testid="rate-diesel-input" value={rates.diesel_per_litre} onChange={(e) => setRates({ ...rates, diesel_per_litre: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label className="label-cap">DG Fuel (₹/L)</Label><Input type="number" step="0.01" data-testid="rate-dg-input" value={rates.dg_fuel_per_litre} onChange={(e) => setRates({ ...rates, dg_fuel_per_litre: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label className="label-cap">Effective From</Label><Input type="date" data-testid="rate-effective-input" value={rates.effective_from} onChange={(e) => setRates({ ...rates, effective_from: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={saveRates} className="bg-sky-900 hover:bg-sky-950" data-testid="rate-save-button">Save rates</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
